import React, { useState } from 'react';
import { Checkbox } from '@carbon/react';

interface GroupPermission {
  id: string;
  name: string;
  permissions: {
    jobs: boolean;
    ingest: boolean;
    watchfolder: boolean;
    recorder: boolean;
    admin: boolean;
    analytics: boolean;
  };
}

const seedPermissions: GroupPermission[] = [
  {
    id: 'admins',
    name: 'Administrators',
    permissions: { jobs: true, ingest: true, watchfolder: true, recorder: true, admin: true, analytics: true },
  },
  {
    id: 'ops',
    name: 'Operations',
    permissions: { jobs: true, ingest: true, watchfolder: true, recorder: false, admin: false, analytics: true },
  },
  {
    id: 'viewers',
    name: 'Viewers',
    permissions: { jobs: true, ingest: false, watchfolder: false, recorder: false, admin: false, analytics: true },
  },
];

const PermissionCheckbox: React.FC<{
  checked: boolean;
  onChange: (value: boolean) => void;
  ariaLabel: string;
}> = ({ checked, onChange, ariaLabel }) => (
  <div className="settings-page__permissions-cell">
    <Checkbox
      id={`${ariaLabel}-${Math.random().toString(36).slice(2)}`}
      labelText=""
      checked={checked}
      onChange={(_, { checked }) => onChange(checked)}
      aria-label={ariaLabel}
    />
  </div>
);

const PermissionsTab: React.FC = () => {
  const [groupPermissions, setGroupPermissions] = useState<GroupPermission[]>(seedPermissions);

  const togglePermission = (groupId: string, key: keyof GroupPermission['permissions']) => (value: boolean) => {
    setGroupPermissions(prev =>
      prev.map(group =>
        group.id === groupId ? { ...group, permissions: { ...group.permissions, [key]: value } } : group
      )
    );
  };

  return (
    <div className="settings-page__panel">
      <div className="settings-page__panel-header">
        <div>
          <h3>Permissions</h3>
          <p>Manage user permissions and access controls.</p>
        </div>
      </div>
      <div className="settings-page__permissions-table">
        <div className="settings-page__permissions-row settings-page__permissions-row--header">
          <div>Group</div>
          <div>Job Monitor</div>
          <div>Ingest</div>
          <div>Watchfolder</div>
          <div>Recorder</div>
          <div>Admin</div>
          <div>Viewer</div>
        </div>
        {groupPermissions.map(group => (
          <div className="settings-page__permissions-row" key={group.id}>
            <div className="settings-page__permissions-group">{group.name}</div>
            <PermissionCheckbox
              ariaLabel={`jobs-${group.id}`}
              checked={group.permissions.jobs}
              onChange={togglePermission(group.id, 'jobs')}
            />
            <PermissionCheckbox
              ariaLabel={`ingest-${group.id}`}
              checked={group.permissions.ingest}
              onChange={togglePermission(group.id, 'ingest')}
            />
            <PermissionCheckbox
              ariaLabel={`watchfolder-${group.id}`}
              checked={group.permissions.watchfolder}
              onChange={togglePermission(group.id, 'watchfolder')}
            />
            <PermissionCheckbox
              ariaLabel={`recorder-${group.id}`}
              checked={group.permissions.recorder}
              onChange={togglePermission(group.id, 'recorder')}
            />
            <PermissionCheckbox
              ariaLabel={`admin-${group.id}`}
              checked={group.permissions.admin}
              onChange={togglePermission(group.id, 'admin')}
            />
            <PermissionCheckbox
              ariaLabel={`viewer-${group.id}`}
              checked={group.permissions.analytics}
              onChange={togglePermission(group.id, 'analytics')}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default PermissionsTab;
